import React, { useState } from 'react';

const renderField = (fieldDef, compositeKey, value, error, onChange) => {
  const field = fieldDef.field;
  const fieldValue = value || '';
  const fieldError = error;
  const required = fieldDef.required;
  const visible = fieldDef.visible;
  const ancho = Number(fieldDef.ancho || 12);
  const span = Number.isNaN(ancho) ? 12 : Math.min(12, Math.max(1, ancho));
  const wrapperStyle = { gridColumn: `span ${span}` };

  if (visible === false) return null;

  switch (field.type) {
    case 'text':
    case 'string':
      return (
        <div key={compositeKey} className="form-group" style={wrapperStyle}>
          <label htmlFor={compositeKey}>
            {field.name} {required && <span style={{ color: '#d32f2f' }}>*</span>}
          </label>
          <input
            type="text"
            id={compositeKey}
            value={fieldValue}
            onChange={(e) => onChange(compositeKey, e.target.value)}
            className={fieldError ? 'error' : ''}
          />
          {fieldError && <span className="error-message">{fieldError}</span>}
        </div>
      );

    case 'textarea':
      return (
        <div key={compositeKey} className="form-group" style={wrapperStyle}>
          <label htmlFor={compositeKey}>
            {field.name} {required && <span style={{ color: '#d32f2f' }}>*</span>}
          </label>
          <textarea
            id={compositeKey}
            value={fieldValue}
            onChange={(e) => onChange(compositeKey, e.target.value)}
            className={fieldError ? 'error' : ''}
            rows="3"
          />
          {fieldError && <span className="error-message">{fieldError}</span>}
        </div>
      );

    case 'number':
    case 'decimal':
      return (
        <div key={compositeKey} className="form-group" style={wrapperStyle}>
          <label htmlFor={compositeKey}>
            {field.name} {required && <span style={{ color: '#d32f2f' }}>*</span>}
          </label>
          <input
            type="number"
            step="0.01"
            id={compositeKey}
            value={fieldValue}
            onChange={(e) => onChange(compositeKey, e.target.value)}
            className={fieldError ? 'error' : ''}
          />
          {fieldError && <span className="error-message">{fieldError}</span>}
        </div>
      );

    case 'date':
      return (
        <div key={compositeKey} className="form-group" style={wrapperStyle}>
          <label htmlFor={compositeKey}>
            {field.name} {required && <span style={{ color: '#d32f2f' }}>*</span>}
          </label>
          <input
            type="date"
            id={compositeKey}
            value={fieldValue}
            onChange={(e) => onChange(compositeKey, e.target.value)}
            className={fieldError ? 'error' : ''}
          />
          {fieldError && <span className="error-message">{fieldError}</span>}
        </div>
      );

    case 'select':
      return (
        <div key={compositeKey} className="form-group" style={wrapperStyle}>
          <label htmlFor={compositeKey}>
            {field.name} {required && <span style={{ color: '#d32f2f' }}>*</span>}
          </label>
          <select
            id={compositeKey}
            value={fieldValue}
            onChange={(e) => onChange(compositeKey, e.target.value)}
            className={fieldError ? 'error' : ''}
          >
            <option value="">Seleccionar...</option>
            {field.options && field.options.map(option => (
              <option key={option.value || option.id || option} value={option.value || option.id || option}>
                {option.label || option.name || option}
              </option>
            ))}
          </select>
          {fieldError && <span className="error-message">{fieldError}</span>}
        </div>
      );

    case 'boolean':
      return (
        <div key={compositeKey} className="form-group" style={wrapperStyle}>
          <label className="checkbox-label">
            <input
              type="checkbox"
              id={compositeKey}
              checked={fieldValue === 'true' || fieldValue === true}
              onChange={(e) => onChange(compositeKey, e.target.checked.toString())}
              className={fieldError ? 'error' : ''}
            />
            <span className="checkbox-text">
              {field.name} {required && <span style={{ color: '#d32f2f' }}>*</span>}
            </span>
          </label>
          {fieldError && <span className="error-message">{fieldError}</span>}
        </div>
      );

    default:
      return (
        <div key={compositeKey} className="form-group" style={wrapperStyle}>
          <label htmlFor={compositeKey}>
            {field.name} {required && <span style={{ color: '#d32f2f' }}>*</span>}
          </label>
          <input
            type="text"
            id={compositeKey}
            value={fieldValue}
            onChange={(e) => onChange(compositeKey, e.target.value)}
            className={fieldError ? 'error' : ''}
          />
          {fieldError && <span className="error-message">{fieldError}</span>}
        </div>
      );
  }
};

const SectionCards = ({ sections, values, onChange, errors, completedSections, onSectionComplete }) => {
  const [openSectionIndex, setOpenSectionIndex] = useState(null);
  const [sectionErrors, setSectionErrors] = useState({});

  if (!sections || sections.length === 0) {
    return null;
  }

  const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));

  const getCompositeKey = (sectionIndex, fieldId) => {
    return `section_${sectionIndex}_field_${fieldId}`;
  };

  const toggleSection = (index) => {
    setOpenSectionIndex(openSectionIndex === index ? null : index);
    setSectionErrors(prev => ({ ...prev, [index]: {} }));
  };

  const validateSection = (sectionIndex, section) => {
    const newErrors = {};
    let isValid = true;

    (section.fields || []).forEach(fieldDef => {
      if (fieldDef.visible === false) return;
      if (!fieldDef.required) return;

      const key = getCompositeKey(sectionIndex, fieldDef.field.id);
      const val = values[key];

      if (!val || val === '') {
        newErrors[key] = `${fieldDef.field.name} es requerido`;
        isValid = false;
      }
    });

    return { isValid, errors: newErrors };
  };

  const handleSaveSection = (sectionIndex, section) => {
    const { isValid, errors: validationErrors } = validateSection(sectionIndex, section);

    if (isValid) {
      setSectionErrors(prev => ({ ...prev, [sectionIndex]: {} }));
      onSectionComplete(sectionIndex, true);
      setOpenSectionIndex(null);
    } else {
      setSectionErrors(prev => ({ ...prev, [sectionIndex]: validationErrors }));
    }
  };

  const getCompletedFieldsCount = (sectionIndex, section) => {
    let filled = 0;
    let total = 0;
    (section.fields || []).forEach(fieldDef => {
      if (fieldDef.visible === false) return;
      total++;
      const key = getCompositeKey(sectionIndex, fieldDef.field.id);
      if (values[key] && values[key] !== '') filled++;
    });
    return { filled, total };
  };

  return (
    <div className="section-cards">
      {sortedSections.map((section, idx) => {
        const isOpen = openSectionIndex === idx;
        const isCompleted = completedSections[idx] === true;
        const { filled, total } = getCompletedFieldsCount(idx, section);
        const currentSectionErrors = { ...sectionErrors[idx], ...errors };
        const visibleFields = (section.fields || []).filter(f => f.visible !== false);

        return (
          <div
            key={idx}
            className={`section-card ${isOpen ? 'section-card--open' : ''} ${isCompleted ? 'section-card--completed' : ''}`}
          >
            <div
              className="section-card__header"
              onClick={() => toggleSection(idx)}
            >
              <div className="section-card__header-left">
                {isCompleted ? (
                  <span className="section-card__check">&#10003;</span>
                ) : (
                  <span className="section-card__index">{idx + 1}</span>
                )}
                <span className="section-card__title">{section.name}</span>
              </div>
              <div className="section-card__header-right">
                <span className="section-card__badge">
                  {isCompleted ? 'Completado' : `${filled}/${total}`}
                </span>
                <span className={`section-card__arrow ${isOpen ? 'section-card__arrow--open' : ''}`}>
                  &#9662;
                </span>
              </div>
            </div>

            {isOpen && (
              <div className="section-card__body">
                <div className="section-card__fields">
                  {visibleFields
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map(fieldDef => {
                      const key = getCompositeKey(idx, fieldDef.field.id);
                      return renderField(
                        fieldDef,
                        key,
                        values[key],
                        currentSectionErrors[key],
                        onChange
                      );
                    })}
                </div>
                <div className="section-card__actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleSaveSection(idx, section)}
                  >
                    Guardar sección
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default SectionCards;
